import React from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import type { StyleClasses } from '../../styles'

const useStyles = makeStyles<Theme, DonutChartProps>((theme) => ({
  root: {
    margin: '0 auto',
    borderRadius: '50%',
    display: 'block'
  },
  track: {
    fill: 'transparent',
    stroke: theme.paintscout?.palette?.brand?.Neutral?.P96
  },
  indicator: {
    fill: 'transparent',
    strokeDasharray: '0 10000',
    transition: 'stroke-dasharray .3s ease',
    stroke: theme.paintscout?.palette?.secondary?.['140']?.C70 || theme.palette.success.main
  },
  text: {
    ...theme.typography.h3,
    fill: theme.typography.h3.color
  }
}))

export interface DonutChartProps {
  classes?: StyleClasses<typeof useStyles>
  size?: { width?: number; height?: number }
  value: number
  loading?: boolean
  strokeWidth?: number
  maxValue?: number
}

function DonutChart(props: DonutChartProps) {
  const classes = useStyles(props)
  const { value = 0, maxValue = 100, loading, strokeWidth = 10, size } = props
  const donutSize = size?.width ?? 0
  const halfsize = donutSize * 0.5
  const radius = halfsize - strokeWidth * 0.5
  const circumference = 2 * Math.PI * radius
  const strokeval = ((loading ? 0 : value) * circumference) / maxValue
  const dashval = strokeval + ' ' + circumference

  const trackstyle = { strokeWidth }
  const indicatorstyle = { strokeWidth, strokeDasharray: dashval }
  const rotation = 'rotate(270 ' + halfsize + ',' + halfsize + ')'

  const fontSize = donutSize / 4.5

  const displayValue = (Math.round((value / maxValue) * 100) * 100) / 100
  return (
    <div>
      <svg width={donutSize} height={donutSize} className={classes.root}>
        <circle
          r={radius}
          cx={halfsize}
          cy={halfsize}
          transform={rotation}
          style={trackstyle}
          className={classes.track}
        />
        <circle
          r={radius}
          cx={halfsize}
          cy={halfsize}
          transform={rotation}
          style={indicatorstyle}
          className={classes.indicator}
        />
        <text x={halfsize} y={halfsize} style={{ textAnchor: 'middle' }}>
          <tspan className={classes.text} x={halfsize} y={halfsize + fontSize / Math.PI} style={{ fontSize }}>
            {loading ? `-%` : `${isNaN(displayValue) ? 0 : displayValue}%`}
          </tspan>
        </text>
      </svg>
    </div>
  )
}

export default DonutChart
